import { useState } from "react";
import { motion } from "framer-motion";
import { AuthShell } from "../components/AuthShell";
import { PhoneShowcase } from "../components/PhoneShowcase";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { PasswordStrengthMeter } from "../components/PasswordStrengthMeter";
import { passwordPolicyError } from "../lib/password";
import { DURATION, EASE_OUT_SOFT } from "../lib/motion";

/**
 * BD1 — create-password step for a NEW email (check-email said exists:false).
 *
 * Collects password + confirm (policy mirrors the server), then triggers
 * /api/otp/send and hands the password UP to LoginRoute — the account is
 * only created by /api/auth/signup AFTER the emailed code is verified on the
 * next screen, so nobody can register an inbox they can't read. No password
 * ever touches storage or logs; it lives in route state for one screen hop.
 */
interface CreatePasswordScreenProps {
  email: string;
  /** OTP sent; parent stores the chosen password and advances to code entry. */
  onSent: (password: string) => void;
  onBack: () => void;
}

export function CreatePasswordScreen({ email, onSent, onBack }: CreatePasswordScreenProps) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const policyError = touched ? passwordPolicyError(password) : null;
  const confirmError =
    touched && confirm && confirm !== password ? "Passwords don't match." : null;
  const canSubmit =
    !passwordPolicyError(password) && confirm === password && !loading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    setSubmitError(null);
    if (!canSubmit) return;
    setLoading(true);
    try {
      const r = await fetch("/api/otp/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setSubmitError(
          (data && (data.message || data.error)) ||
            "Could not send the verification code. Please try again.",
        );
        setLoading(false);
        return;
      }
      setLoading(false);
      onSent(password);
    } catch {
      setSubmitError("Network error. Please check your connection and try again.");
      setLoading(false);
    }
  }

  return (
    <AuthShell
      eyebrow="Create account · Free 7-day trial"
      headline="Insure smarter, live better."
      subhead="BeyondSure is India's most intelligent AI insurance advisor — in English, Hindi, and 10+ Indian languages."
      formTitle="Create your password."
      showcase={<PhoneShowcase />}
    >
      <motion.form
        onSubmit={handleSubmit}
        noValidate
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: DURATION.base, ease: EASE_OUT_SOFT }}
        className="bsure-ui flex flex-col gap-5"
      >
        <p className="bsure-prose text-[13.5px] leading-[1.55] text-[var(--color-ink-60)]">
          New account for{" "}
          <span className="text-[var(--color-ink)] font-medium">{email}</span>.
          Choose a password — we'll email a code to confirm it's really you.
        </p>

        {submitError ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[13px] leading-snug text-red-900"
          >
            {submitError}
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <Input
            label="Password"
            type="password"
            autoComplete="new-password"
            autoFocus
            placeholder="At least 8 characters, a letter and a number"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched(true)}
            error={policyError}
          />
          <PasswordStrengthMeter password={password} />
        </div>

        <Input
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          placeholder="Type it again"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          onBlur={() => setTouched(true)}
          error={confirmError}
        />

        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={loading}
          disabled={!password || !confirm}
        >
          Continue
        </Button>

        <button
          type="button"
          onClick={onBack}
          className="bsure-ui mt-1 text-[12.5px] text-[var(--color-ink-60)] hover:text-[var(--color-ink)] transition-colors self-start"
        >
          ← Use a different email
        </button>
      </motion.form>
    </AuthShell>
  );
}

export default CreatePasswordScreen;
